import { useLDClient } from '@launchdarkly/react-sdk';
import { useDarklyImpl } from 'integrations/launchDarkly/impl/useDarklyImpl';
import { OcDarklyFlags } from './__generated__/darkly.flags';

export { OcDarklyFlags };

/**
 * Hook to get the value of one or more boolean flags from LaunchDarkly.
 * @param flagNames OcDarklyFlags.BooleanFlag[] - The names of the boolean flags to retrieve.
 * @returns An object containing the values of the requested boolean flags.
 * @example
 *  const {
 *    uiNewInvoiceSettingsPage,
 *    abacAttributePreload
 *  } = useOcDarklyBoolean(OcDarklyFlags.BooleanFlag.uiNewInvoiceSettingsPage, OcDarklyFlags.BooleanFlag.abacAttributePreload);
 */
export function useOcDarklyBoolean<FLAG_NAME extends OcDarklyFlags.BooleanFlag>(...flagNames: FLAG_NAME[]) {
  const client = useLDClient();

  return useDarklyImpl(client, flagNames, 'boolean');
}

/**
 * Hook to get the value of one or more string flags from LaunchDarkly.
 * @param flagNames OcDarklyFlags.StringFlag[] - The names of the string flags to retrieve.
 * @returns An object containing the values of the requested string flags.
 * @example
 *  const {
 *    SENTRY_SAMPLING_RATE,
 *    API_URL
 *  } = useOcDarklyString(OcDarklyFlags.StringFlag.SENTRY_SAMPLING_RATE, OcDarklyFlags.StringFlag.API_URL);
 */
export function useOcDarklyString<FLAG_NAME extends OcDarklyFlags.StringFlag>(...flagNames: FLAG_NAME[]) {
  const client = useLDClient();

  return useDarklyImpl(client, flagNames, 'string');
}

/**
 * Hook to get the value of one or more number flags from LaunchDarkly.
 * @param flagNames OcDarklyFlags.NumberFlag[] - The names of the number flags to retrieve.
 * @returns An object containing the values of the requested number flags.
 * @example
 *  const {
 *    suggesterSimilarityThreshold,
 *    maxItemsPerPage
 *  } = useOcDarklyNumber(OcDarklyFlags.NumberFlag.suggesterSimilarityThreshold, OcDarklyFlags.NumberFlag.maxItemsPerPage);
 */
export function useOcDarklyNumber<FLAG_NAME extends OcDarklyFlags.NumberFlag>(...flagNames: FLAG_NAME[]) {
  const client = useLDClient();

  return useDarklyImpl(client, flagNames, 'number');
}
